import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { formatBytes } from '../backup/presentation';
import type { ChangelogEntry, CheckState, JobStatus, SystemStatus } from './api';
import { useSystemStatus } from './queries';

/**
 * Die Statusseite -- erreichbar ueber die Build-Nummer unten in der
 * Navigation.
 *
 * **Oben die eine Aussage, darunter die Gruende.** Wer hierherkommt, will
 * zuerst wissen, ob alles in Ordnung ist, und erst dann, was nicht. Die
 * Ampel oben ist der schlechteste Einzelzustand; die Zeile darunter nennt,
 * welche Abschnitte ihn verursachen, damit niemand sie suchen muss.
 *
 * **Farbe nie allein.** Jeder Zustand steht als Wort im Abzeichen (docs/02,
 * Abschnitt 5); die Farbe unterstreicht nur.
 */
export function StatusPage() {
  const { t, i18n } = useTranslation();
  const status = useSystemStatus();
  const data = status.data;

  return (
    <>
      <PageHeader
        title={t('system.title')}
        subtitle={
          data
            ? t('system.checkedAt', {
                when: new Date(data.checked_at).toLocaleTimeString(i18n.language),
              })
            : t('system.subtitle')
        }
        action={
          <Button
            variant="secondary"
            disabled={status.isFetching}
            onClick={() => {
              void status.refetch();
            }}
          >
            {status.isFetching ? t('system.checking') : t('system.checkNow')}
          </Button>
        }
      />

      {status.isError ? <ErrorNotice error={status.error} /> : null}
      {status.isPending ? <SkeletonTable columns={2} rows={6} /> : null}

      {data ? (
        <>
          <OverallBanner status={data} />
          <div className="status-grid">
            <VersionCard status={data} />
            <ServerCard status={data} />
            <BackupCard status={data} />
            <DatabaseCard status={data} />
            <ServicesCard status={data} />
            <HostCard status={data} />
          </div>
          <ChangelogCard entries={data.changelog} />
        </>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

const TONES: Record<CheckState, BadgeTone> = {
  ok: 'success',
  warning: 'warning',
  critical: 'danger',
  unknown: 'neutral',
};

function StateBadge({ state }: { state: CheckState }) {
  const { t } = useTranslation();
  return (
    <Badge tone={TONES[state]} dot>
      {t(`system.state.${state}`)}
    </Badge>
  );
}

function Card({
  title,
  state,
  children,
}: {
  title: string;
  state?: CheckState;
  children: React.ReactNode;
}) {
  return (
    <section className="card">
      <div className="card__header">
        <h2>{title}</h2>
        {state ? <StateBadge state={state} /> : null}
      </div>
      {children}
    </section>
  );
}

/** Begriff und Wert, untereinander. */
function Facts({ rows }: { rows: readonly (readonly [string, React.ReactNode])[] }) {
  return (
    <dl className="status-facts">
      {rows.map(([term, value]) => (
        <div key={term} className="status-facts__row">
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Ein Balken fuer eine Auslastung in Prozent. */
function Meter({
  label,
  percent,
  state,
  detail,
}: {
  label: string;
  percent: number | null | undefined;
  state: CheckState;
  detail?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const known = percent !== null && percent !== undefined;
  return (
    <div className="status-meter">
      <div className="status-meter__head">
        <span>{label}</span>
        <span>
          {known
            ? `${percent.toLocaleString(i18n.language, { maximumFractionDigits: 1 })} %`
            : t('system.notMeasured')}
        </span>
      </div>
      <div
        className={`status-meter__track status-meter__track--${state}`}
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={known ? percent : undefined}
      >
        <span style={{ width: `${known ? Math.min(percent, 100) : 0}%` }} />
      </div>
      {detail ? <p className="status-note">{detail}</p> : null}
    </div>
  );
}

function useFormat() {
  const { t, i18n } = useTranslation();
  return {
    bytes: (value: number | null | undefined) =>
      formatBytes(value, i18n.language) ?? t('system.notMeasured'),
    dateTime: (value: string | null | undefined) =>
      value ? new Date(value).toLocaleString(i18n.language) : t('system.never'),
    duration: (seconds: number | null | undefined) => {
      if (seconds === null || seconds === undefined) return t('system.notMeasured');
      const days = Math.floor(seconds / 86_400);
      const hours = Math.floor((seconds % 86_400) / 3_600);
      return t('system.uptimeValue', { days, hours });
    },
  };
}

// ---------------------------------------------------------------------------
// Die Abschnitte
// ---------------------------------------------------------------------------

/** Die Abschnitte, die nicht gruen sind -- in der Reihenfolge der Seite. */
function problems(status: SystemStatus): string[] {
  const keys: [string, CheckState][] = [
    ['cpu', status.cpu.state],
    ['memory', status.memory.state],
    ...status.disks.map((disk): [string, CheckState] => ['disk', disk.state]),
    ['backup', status.backup.state],
    ['restoreCheck', status.restore_check.state],
    ['database', status.database.state],
    ...status.services.map((service): [string, CheckState] => ['services', service.state]),
    ['certificate', status.certificate?.state ?? 'ok'],
  ];
  const seen = new Set<string>();
  for (const [key, state] of keys) {
    if (state === 'warning' || state === 'critical') seen.add(key);
  }
  return [...seen];
}

function OverallBanner({ status }: { status: SystemStatus }) {
  const { t } = useTranslation();
  const affected = problems(status);
  return (
    <div className={`status-banner status-banner--${status.state}`} role="status">
      <strong>{t(`system.overall.${status.state}`)}</strong>
      {affected.length > 0 ? (
        <span>
          {t('system.affected', {
            list: affected.map((key) => t(`system.section.${key}`)).join(', '),
          })}
        </span>
      ) : null}
    </div>
  );
}

function VersionCard({ status }: { status: SystemStatus }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { version } = status;
  return (
    <Card title={t('system.section.version')}>
      <p className="status-version">
        {version.build !== null ? t('system.build', { build: version.build }) : version.version}
      </p>
      <Facts
        rows={[
          [t('system.commit'), <code key="c">{version.commit ?? '—'}</code>],
          [t('system.committedAt'), format.dateTime(version.committed_at)],
          [t('system.appStartedAt'), format.dateTime(status.host.app_started_at)],
          [t('system.packageVersion'), version.version],
        ]}
      />
    </Card>
  );
}

function ServerCard({ status }: { status: SystemStatus }) {
  const { t, i18n } = useTranslation();
  const format = useFormat();
  const { cpu, memory } = status;
  const worst = [cpu.state, memory.state, ...status.disks.map((disk) => disk.state)];
  const state: CheckState = worst.includes('critical')
    ? 'critical'
    : worst.includes('warning')
      ? 'warning'
      : worst.every((item) => item === 'unknown')
        ? 'unknown'
        : 'ok';

  // Last je Kern als Prozent: 1,0 je Kern ist "voll ausgelastet".
  const cpuPercent = cpu.load_5 !== null && cpu.cores ? (cpu.load_5 / cpu.cores) * 100 : null;

  return (
    <Card title={t('system.section.server')} state={state}>
      <Meter
        label={t('system.section.cpu')}
        percent={cpuPercent}
        state={cpu.state}
        detail={
          cpu.load_1 !== null
            ? t('system.cpuDetail', {
                load1: cpu.load_1.toLocaleString(i18n.language),
                load5: (cpu.load_5 ?? 0).toLocaleString(i18n.language),
                load15: (cpu.load_15 ?? 0).toLocaleString(i18n.language),
                cores: cpu.cores ?? '?',
              })
            : null
        }
      />
      <Meter
        label={t('system.section.memory')}
        percent={memory.used_percent}
        state={memory.state}
        detail={
          memory.total_bytes
            ? t('system.memoryDetail', {
                available: format.bytes(memory.available_bytes),
                total: format.bytes(memory.total_bytes),
                swap: format.bytes(memory.swap_used_bytes),
              })
            : null
        }
      />
      {status.disks.map((disk) => (
        <Meter
          key={disk.path}
          label={t('system.diskLabel', {
            label: t(`system.disk.${disk.label}`, { defaultValue: disk.label }),
            path: disk.path,
          })}
          percent={disk.used_percent}
          state={disk.state}
          detail={
            disk.total_bytes
              ? t('system.diskDetail', {
                  free: format.bytes(disk.free_bytes),
                  total: format.bytes(disk.total_bytes),
                })
              : null
          }
        />
      ))}
    </Card>
  );
}

function JobFacts({ job, labelKey }: { job: JobStatus; labelKey: string }) {
  const { t } = useTranslation();
  const format = useFormat();
  return (
    <div className="status-job">
      <div className="status-job__head">
        <h3>{t(labelKey)}</h3>
        <StateBadge state={job.state} />
      </div>
      {job.reported ? (
        <Facts
          rows={[
            [t('system.lastRun'), format.dateTime(job.finished_at)],
            [t('system.result'), job.succeeded ? t('system.succeeded') : t('system.failed')],
            ...(job.size_bytes ? [[t('system.size'), format.bytes(job.size_bytes)] as const] : []),
            ...(job.message ? [[t('system.message'), job.message] as const] : []),
          ]}
        />
      ) : (
        <p className="status-note">{t('system.notReported')}</p>
      )}
    </div>
  );
}

function BackupCard({ status }: { status: SystemStatus }) {
  const { t } = useTranslation();
  const state: CheckState =
    status.backup.state === 'critical' || status.restore_check.state === 'critical'
      ? 'critical'
      : status.backup.state === 'ok' && status.restore_check.state === 'ok'
        ? 'ok'
        : 'warning';
  return (
    <Card title={t('system.section.backups')} state={state}>
      <JobFacts job={status.backup} labelKey="system.section.backup" />
      <JobFacts job={status.restore_check} labelKey="system.section.restoreCheck" />
      <p className="status-note">{t('system.backupHint')}</p>
    </Card>
  );
}

function DatabaseCard({ status }: { status: SystemStatus }) {
  const { t, i18n } = useTranslation();
  const format = useFormat();
  const { database, certificate } = status;
  return (
    <Card title={t('system.section.database')} state={database.state}>
      <Facts
        rows={[
          [
            t('system.reachable'),
            database.reachable
              ? t('system.latency', {
                  ms: (database.latency_ms ?? 0).toLocaleString(i18n.language),
                })
              : t('system.unreachable'),
          ],
          [t('system.serverVersion'), database.server_version ?? '—'],
          [t('system.databaseSize'), format.bytes(database.size_bytes)],
          [t('system.schemaRevision'), database.schema_revision ?? '—'],
          [t('system.expectedRevision'), database.expected_revision ?? '—'],
        ]}
      />
      {certificate ? (
        <div className="status-job">
          <div className="status-job__head">
            <h3>{t('system.section.certificate')}</h3>
            <StateBadge state={certificate.state} />
          </div>
          <Facts
            rows={[
              [t('system.certificateHost'), certificate.host],
              [
                t('system.certificateExpires'),
                certificate.expires_at
                  ? t('system.certificateDays', {
                      when: format.dateTime(certificate.expires_at),
                      count: certificate.days_left ?? 0,
                    })
                  : (certificate.error ?? '—'),
              ],
            ]}
          />
        </div>
      ) : null}
    </Card>
  );
}

function ServicesCard({ status }: { status: SystemStatus }) {
  const { t } = useTranslation();
  const states = status.services.map((service) => service.state);
  const state: CheckState = !status.services_available
    ? 'unknown'
    : states.includes('critical')
      ? 'critical'
      : states.includes('warning')
        ? 'warning'
        : 'ok';
  return (
    <Card title={t('system.section.services')} state={state}>
      {!status.services_available ? (
        <p className="status-note">{t('system.servicesUnavailable')}</p>
      ) : status.services.length === 0 ? (
        <p className="status-note">{t('system.servicesNone')}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('system.unit')}</th>
              <th>{t('system.unitState')}</th>
            </tr>
          </thead>
          <tbody>
            {status.services.map((service) => (
              <tr key={service.name}>
                <td>
                  <code>{service.name}</code>
                </td>
                <td>
                  <Badge tone={TONES[service.state]} dot>
                    {`${service.active_state} (${service.sub_state})`}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function HostCard({ status }: { status: SystemStatus }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { host } = status;
  return (
    <Card title={t('system.section.host')}>
      <Facts
        rows={[
          [t('system.hostname'), host.hostname],
          [t('system.platform'), host.platform],
          [t('system.python'), host.python_version],
          [t('system.hostUptime'), format.duration(host.host_uptime_seconds)],
        ]}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Aenderungsverlauf
// ---------------------------------------------------------------------------

/** Was fuer Anwender zaehlt. Der Rest -- Tests, Doku, Pflege -- ist ein Klick entfernt. */
const USER_FACING = new Set(['feat', 'fix', 'perf']);

/** Wie weit die Zusammenfassung oben zurueckblickt. */
const SUMMARY_DAYS = 14;

function ChangelogCard({ entries }: { entries: readonly ChangelogEntry[] }) {
  const { t, i18n } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const since = Date.now() - SUMMARY_DAYS * 86_400_000;
  const recent = entries.filter((entry) => new Date(entry.committed_at).getTime() >= since);
  const added = recent.filter((entry) => entry.kind === 'feat').length;
  const fixed = recent.filter((entry) => entry.kind === 'fix').length;

  const visible = showAll ? entries : entries.filter((entry) => USER_FACING.has(entry.kind));

  // Nach Tag gruppiert: "Was kam am Dienstag?" ist die Frage, mit der man
  // hier nachsieht.
  const days = new Map<string, ChangelogEntry[]>();
  for (const entry of visible) {
    const day = new Date(entry.committed_at).toLocaleDateString(i18n.language, {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    days.set(day, [...(days.get(day) ?? []), entry]);
  }

  return (
    <section className="card">
      <div className="card__header">
        <h2>{t('system.section.changelog')}</h2>
        <Button
          variant="ghost"
          onClick={() => {
            setShowAll((value) => !value);
          }}
        >
          {showAll ? t('system.changelogUserFacing') : t('system.changelogAll')}
        </Button>
      </div>
      <p className="status-note">
        {t('system.changelogSummary', { days: SUMMARY_DAYS, added, fixed })}
      </p>

      {entries.length === 0 ? (
        <p className="status-note">{t('system.changelogUnavailable')}</p>
      ) : (
        [...days.entries()].map(([day, items]) => (
          <div key={day} className="status-changelog__day">
            <h3>{day}</h3>
            <ul className="status-changelog">
              {items.map((entry) => (
                <li key={entry.commit}>
                  <Badge
                    tone={
                      entry.kind === 'feat' ? 'info' : entry.kind === 'fix' ? 'success' : 'neutral'
                    }
                  >
                    {t(`system.kind.${entry.kind}`, { defaultValue: entry.kind })}
                  </Badge>
                  {entry.scope ? (
                    <span className="status-changelog__scope">{entry.scope}</span>
                  ) : null}
                  <span>{entry.summary}</span>
                  <span className="status-changelog__build">
                    {t('system.build', { build: entry.build })}
                    {entry.pull_request ? ` · #${entry.pull_request}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}
